'use client';

import { useQuery } from '@tanstack/react-query';
import type {
  DocumentSearchPassage,
  DocumentSearchResponse,
  DocumentSearchResult,
  FileEntryKind,
} from 'pc-types';
import { editorTabHref } from '@/components/editor-tabs/tab-registry';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { unwrapLambdaResponse } from '@/lib/api';
import { useAccessToken } from '@/lib/auth-client';
import { BACKEND_URL } from '@/lib/constants';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 150;

/**
 * Word search over the text inside files, decks, and sheets. spaceId null
 * searches every space the user can open.
 */
export function useDocumentSearch(
  query: string,
  options: {
    spaceId: string | null;
    /** Only this kind of document; null (default) searches all of them. */
    kind?: FileEntryKind | null;
    limit?: number;
    enabled?: boolean;
  }
) {
  const { getAccessToken, loading: authLoading } = useAccessToken();
  const { spaceId, kind = null, limit = 20, enabled = true } = options;
  const current = query.trim();
  const debounced = useDebouncedValue(current, DEBOUNCE_MS);
  const active = current.length >= MIN_QUERY_LENGTH;

  const search = useQuery({
    queryKey: [
      'document-search',
      spaceId ?? 'all',
      kind ?? 'any',
      debounced,
      limit,
    ],
    queryFn: async () => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const url = new URL(`${BACKEND_URL}/search`);
      url.searchParams.set('q', debounced);
      url.searchParams.set('limit', String(limit));
      if (spaceId) url.searchParams.set('spaceId', spaceId);
      if (kind) url.searchParams.set('kind', kind);
      const res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Search failed');
      return unwrapLambdaResponse<DocumentSearchResponse>(await res.json());
    },
    enabled: enabled && !authLoading && debounced.length >= MIN_QUERY_LENGTH,
    staleTime: 30_000,
    // Keep the last results up while the text changes, so typing doesn't
    // flicker, but not across a space or filter change: those results would
    // be the wrong documents, still clickable until the new ones arrive.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === (spaceId ?? 'all') &&
      previousQuery?.queryKey[2] === (kind ?? 'any')
        ? previous
        : undefined,
  });

  return {
    active,
    results: active ? (search.data?.results ?? []) : [],
    isLoading: search.isLoading,
    isError: search.isError,
    // Typing ahead of the debounce, or a request in flight.
    isSearching: active && (debounced !== current || search.isFetching),
  };
}

/** Opens the document at the passage: its page, slide, or sheet cell. */
export function documentSearchHref(
  result: Pick<DocumentSearchResult, 'documentId' | 'kind'>,
  passage?: DocumentSearchPassage
): string {
  const base = editorTabHref({
    documentId: result.documentId,
    kind: result.kind,
  });
  if (!passage) return base;
  const params = new URLSearchParams();
  if (result.kind === 'file' && passage.page !== null) {
    params.set('page', String(passage.page));
  } else if (result.kind === 'deck' && passage.slideId) {
    params.set('slide', passage.slideId);
  } else if (result.kind === 'workbook' && passage.sheetId) {
    params.set('sheet', passage.sheetId);
    if (passage.cell) params.set('cell', passage.cell);
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
